/**
 * useUndoManager - Per-user undo/redo for collaborative editing
 *
 * Uses Yjs UndoManager with trackedOrigins to ensure:
 * - Cmd+Z only undoes YOUR changes (not other collaborators')
 * - Undo stack is 50 actions deep (per CONTEXT.md)
 * - Session-only (clears on refresh per CONTEXT.md)
 * - Groups rapid edits together (captureTimeout)
 *
 * CRITICAL: All document changes MUST use makeChange() wrapper
 * so they're tracked with the user's origin.
 *
 * @param ydoc - The Y.Doc to track
 * @param userId - Current user's ID (used as transaction origin)
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as Y from 'yjs';

const DEBUG_COLLAB = (() => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem('DEBUG_COLLAB') === '1';
  } catch {
    return false;
  }
})();

const debugLog = (...args: any[]) => {
  if (DEBUG_COLLAB) {
    console.log(...args);
  }
};

const makeThrottledLogger = (intervalMs: number) => {
  let last = 0;
  let suppressed = 0;
  return (...args: any[]) => {
    if (!DEBUG_COLLAB) return;
    const now = Date.now();
    if (now - last >= intervalMs) {
      if (suppressed > 0) {
        console.log(...args, `(+${suppressed} suppressed)`);
      } else {
        console.log(...args);
      }
      last = now;
      suppressed = 0;
    } else {
      suppressed++;
    }
  };
};

const logMakeChange = makeThrottledLogger(1000);
const logAfterTransact = makeThrottledLogger(1000);

interface UseUndoManagerReturn {
  // Can we undo?
  canUndo: boolean;
  // Can we redo?
  canRedo: boolean;
  // Undo last change
  undo: () => void;
  // Redo last undone change
  redo: () => void;
  // Wrapper for making tracked changes
  makeChange: (fn: () => void) => void;
  // Number of items in undo stack (for UI display)
  undoStackSize: number;
  // Number of items in redo stack
  redoStackSize: number;
}

export function useUndoManager(
  ydoc: Y.Doc,
  userId: string
): UseUndoManagerReturn {
  // Track undo/redo state for reactive updates
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [undoStackSize, setUndoStackSize] = useState(0);
  const [redoStackSize, setRedoStackSize] = useState(0);

  // Store current userId in ref for stable makeChange callback
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  // Create UndoManager ONCE per ydoc (don't recreate on userId change)
  // We'll update trackedOrigins separately when userId changes
  const undoManagerId = useRef(Math.random().toString(36).substring(7));
  const undoManager = useMemo(() => {
    // Track all Y structures used in the board:
    // - 'nodes' and 'edges' are used by React Flow canvas (BoardPage.tsx)
    // - 'canvas', 'document', 'agentMessages' reserved for Phase 4/5 content
    const scope = [
      ydoc.getMap('nodes'),       // React Flow nodes (used in BoardPage)
      ydoc.getMap('edges'),       // React Flow edges (used in BoardPage)
      ydoc.getArray('canvas'),    // Future: generic canvas items
      ydoc.getMap('document'),    // Future: Tiptap document
      ydoc.getArray('agentMessages'), // Future: AI agent messages
    ];

    const um = new Y.UndoManager(scope, {
      // Start with current userId, will be updated if it changes
      trackedOrigins: new Set([userId]),
      // Group rapid edits together (500ms per CONTEXT.md logical operations)
      captureTimeout: 500,
    });

    // Debug: Log when items are added to stack
    um.on('stack-item-added', (event: any) => {
      debugLog('[useUndoManager] Stack item added! origin:', event.origin, 'type:', event.type);
      debugLog('[useUndoManager] Stack size now:', um.undoStack.length);
      debugLog('[useUndoManager] Current trackedOrigins:', Array.from(um.trackedOrigins));
    });

    // Debug: Log when items are popped from stack
    um.on('stack-item-popped', (event: any) => {
      debugLog('[useUndoManager] Stack item popped!', event);
      debugLog('[useUndoManager] After pop - undoStack:', um.undoStack.length, 'redoStack:', um.redoStack.length);
    });

    // Debug: Log when stack is cleared (if this event exists)
    um.on('stack-cleared', (event: any) => {
      debugLog('[useUndoManager] Stack CLEARED!', event);
    });

    // Generate new ID for this UndoManager instance
    const instanceId = Math.random().toString(36).substring(7);
    debugLog(`[useUndoManager] Created instance ${instanceId} for user ${userId}, tracking: nodes, edges, canvas, document, agentMessages`);
    debugLog(`[useUndoManager] Scope Y.Map 'nodes' size:`, ydoc.getMap('nodes').size);
    // @ts-ignore - store instanceId for debugging
    um._instanceId = instanceId;
    return um;
  }, [ydoc]); // Only depend on ydoc, not userId - prevents recreation on userId change

  // Keep trackedOrigins in sync with userId changes (without recreating UndoManager)
  useEffect(() => {
    // Clear old origins and add current userId
    undoManager.trackedOrigins.clear();
    undoManager.trackedOrigins.add(userId);
    debugLog(`[useUndoManager] Updated trackedOrigins to: ${userId}`);

    // Debug: Log stack size periodically
    if (!DEBUG_COLLAB) {
      return;
    }

    const interval = setInterval(() => {
      debugLog(`[useUndoManager] Undo stack size: ${undoManager.undoStack.length}, Redo stack: ${undoManager.redoStack.length}`);
    }, 3000);

    return () => clearInterval(interval);
  }, [undoManager, userId]);

  // Update state when stack changes
  useEffect(() => {
    const updateState = () => {
      setCanUndo(undoManager.canUndo());
      setCanRedo(undoManager.canRedo());
      setUndoStackSize(undoManager.undoStack.length);
      setRedoStackSize(undoManager.redoStack.length);
    };

    // Initial state
    updateState();

    // Listen for changes
    undoManager.on('stack-item-added', updateState);
    undoManager.on('stack-item-popped', updateState);

    return () => {
      undoManager.off('stack-item-added', updateState);
      undoManager.off('stack-item-popped', updateState);
      // NOTE: Don't destroy here - React StrictMode causes double-render
      // which would destroy the UndoManager prematurely.
      // The UndoManager will be garbage collected when ydoc is destroyed.
    };
  }, [undoManager]);

  // Enforce 50-action limit (per CONTEXT.md)
  useEffect(() => {
    const enforceLimit = () => {
      while (undoManager.undoStack.length > 50) {
        undoManager.undoStack.shift();
      }
    };

    undoManager.on('stack-item-added', enforceLimit);
    return () => {
      undoManager.off('stack-item-added', enforceLimit);
    };
  }, [undoManager]);

  // Undo callback
  const undo = useCallback(() => {
    // @ts-ignore - access debug instanceId
    debugLog(`[useUndoManager] UNDO pressed on instance ${undoManager._instanceId} - canUndo: ${undoManager.canUndo()}, stackSize: ${undoManager.undoStack.length}`);
    // Log what's in the stack before undoing
    debugLog(`[useUndoManager] Stack contents:`, undoManager.undoStack.map((item, i) => ({
      index: i,
      // @ts-ignore - accessing internal structure for debugging
      insertions: item.insertions?.size || 0,
      // @ts-ignore
      deletions: item.deletions?.size || 0,
    })));
    if (undoManager.canUndo()) {
      try {
        undoManager.undo();
        debugLog(`[useUndoManager] After UNDO - canUndo: ${undoManager.canUndo()}, stackSize: ${undoManager.undoStack.length}, redoStack: ${undoManager.redoStack.length}`);
      } catch (err) {
        console.error(`[useUndoManager] UNDO ERROR:`, err);
      }
    } else {
      debugLog(`[useUndoManager] UNDO blocked - nothing to undo`);
    }
  }, [undoManager]);

  // Redo callback
  const redo = useCallback(() => {
    debugLog(`[useUndoManager] REDO pressed - canRedo: ${undoManager.canRedo()}, redoStack: ${undoManager.redoStack.length}`);
    if (undoManager.canRedo()) {
      undoManager.redo();
      debugLog(`[useUndoManager] After REDO - canRedo: ${undoManager.canRedo()}, undoStack: ${undoManager.undoStack.length}`);
    } else {
      debugLog(`[useUndoManager] REDO blocked - nothing to redo`);
    }
  }, [undoManager]);

  // Wrapper for making tracked changes
  // IMPORTANT: All document modifications should use this!
  // Uses userIdRef to always get current userId without recreating callback
  const makeChange = useCallback(
    (fn: () => void) => {
      const origin = userIdRef.current;
      // @ts-ignore - access debug instanceId
      logMakeChange(`[useUndoManager] makeChange on instance ${undoManager._instanceId} with origin: ${origin}`);
      logMakeChange(`[useUndoManager] trackedOrigins contains: ${Array.from(undoManager.trackedOrigins).join(', ')}`);
      ydoc.transact(fn, origin);
      logAfterTransact(`[useUndoManager] After transact - canUndo: ${undoManager.canUndo()}, stackSize: ${undoManager.undoStack.length}`);
    },
    [ydoc, undoManager]
  );

  return {
    canUndo,
    canRedo,
    undo,
    redo,
    makeChange,
    undoStackSize,
    redoStackSize,
  };
}

/**
 * Keyboard shortcut hook for undo/redo
 *
 * Handles:
 * - Cmd/Ctrl+Z for undo
 * - Cmd/Ctrl+Shift+Z for redo
 */
export function useUndoShortcuts(
  undo: () => void,
  redo: () => void
): void {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Cmd (Mac) or Ctrl (Windows/Linux)
      const modifier = e.metaKey || e.ctrlKey;

      if (modifier && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);
}
